import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTheme } from "../../../../theme/useTheme";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector } from "react-redux";
import Svg, {
  Circle,
  Line as SvgLine,
  Polyline,
  Rect,
  Text as SvgText,
} from "react-native-svg";
import {
  fetchPeriodWeeks,
  PeriodWeekResponse,
} from "../../../../api/periodWeeksApi";
import { FontFamily } from "../../../../constants/fonts";
import { DLOSimpleBreadcrumb } from "../../../../modules/dlo/components/DLOBreadcrumb";
import DLOTooltip from "../../../../modules/dlo/components/DLOTooltip";
import { useToast } from "../../../../components/LhToastContainer";
import { RootState } from "../../../../store";
import type { EnrichedLocation } from "../../../../store/slices/appDataSlice";
import KPIFilterDropdown, {
  KPIFilterDropdownItem,
} from "../../../AnalyticsHub/components/KPIFilterDropdown";
import { useDisabledPeriodWeekIds } from "../../../AnalyticsHub/hooks/useDisabledPeriodWeekIds";
import { useRegionMarkets } from "../../../../hooks/useRegionMarkets";
import DeliveryDateRangeFilter, {
  DeliveryDateRange,
} from "../../components/DeliveryDateRangeFilter";
import { getValidIconName } from "../../../AnalyticsHub/utils/reportsTabHelpers";
import LoadExecution, { LoadExecutionRow } from "./components/LoadExecution";
import ExecutiveOverviewTab from "./components/ExecutiveOverviewTab";
import LocationSummaryTab, {
  DEFAULT_LOCATION_TABLE_COLUMN_CONFIG,
  DEFAULT_LOCATION_TABLE_COLUMN_SECTION_STATE,
} from "./components/LocationSummaryTab";
import { useOnTimeDispatchKpi } from "./hooks/useOnTimeDispatchKpi";
import { useOnTimeDispatchLatestUpdate } from "./hooks/useOnTimeDispatchLatestUpdate";
import { useLoadExecutionScorecards } from "./hooks/useLoadExecutionScorecards";
import { useLoadExecutionDetail } from "./hooks/useLoadExecutionDetail";
import {
  AppliedFiltersForLoadExecution,
  LoadExecutionDetailRecord,
} from "./services/onTimeDispatchLoadExecutionApi";
import {
  INCOMPLETE_STATUS_FILTER_ITEM,
} from "./utils/incompleteStatusFilter";
import { formatCount, formatPercentage, safeValue } from "./utils/formatUtils";

type OnTimeDispatchTab =
  | "executive-overview"
  | "location-summary"
  | "load-execution";
type FilterKey =
  | "region"
  | "market"
  | "location"
  | "locationType"
  | "year"
  | "period"
  | "week"
  | "deliveryDate"
  | "driver"
  | "status"
  | "onTime"
  | "arrivedExpected";
type QuickSelectKey = "wtd" | "ptd" | "qtd" | "ytd" | "lcw" | "lcp";

const REPORT_LINK = "/navigator-reports/on-time-dispatch";
const WEEKS_PER_PERIOD = 4;
const QUARTER_START_PERIODS = [1, 4, 7, 10];

const QUICK_SELECT_OPTIONS: { key: QuickSelectKey; label: string }[] = [
  { key: "wtd", label: "Week-to-Date" },
  { key: "ptd", label: "Period-to-Date" },
  { key: "qtd", label: "Quarter-to-Date" },
  { key: "ytd", label: "Year-to-Date" },
  { key: "lcw", label: "Last Closed Week" },
  { key: "lcp", label: "Last Closed Period" },
];

const IS_LOAD_EXECUTION_ENABLED = true;

const LOAD_EXECUTION_SEED_ROWS: Array<
  LoadExecutionRow & { year: number; period: number; weekInYear: number }
> = [
  {
    id: "406182300",
    region: "Northcentral",
    market: "Upper Midwest",
    location: "Aberdeen SD",
    locationType: "Warehouse",
    driver: "Steven Grant",
    status: "Complete",
    onTime: true,
    arrivedAsExpected: "Y",
    manifestHour: 1,
    deliveryDate: "2026-08-13",
    localRouteId: "40195-01",
    nationalRouteId: "NR-620611",
    manifestArrivalDateTime: "2026-08-13 01:10",
    actualArrivalDateTime: "2026-08-13 01:05",
    actualCompletedDateTime: "2026-08-13 01:42",
    percentComplete: 100,
    vehicle: "PV-20384",
    orderCount: 16,
    ssccDetails: [],
    year: 2026,
    period: 8,
    weekInYear: 29,
  },
  {
    id: "406182301",
    region: "Northcentral",
    market: "Upper Midwest",
    location: "Aberdeen SD",
    locationType: "Warehouse",
    driver: "Keisha Lambert",
    status: "Incomplete",
    onTime: false,
    arrivedAsExpected: "N",
    manifestHour: 2,
    deliveryDate: "2026-08-13",
    localRouteId: "40196-02",
    nationalRouteId: "NR-620614",
    manifestArrivalDateTime: "2026-08-13 02:20",
    actualArrivalDateTime: "2026-08-13 01:43",
    actualCompletedDateTime: "",
    percentComplete: 84.62,
    vehicle: "PV-20390",
    orderCount: 21,
    ssccDetails: [
      { stop: 1, ssccId: "(00) 1 0614141 19283101 4", status: "Not Loaded", eta: "06:45" },
      { stop: 2, ssccId: "(00) 1 0614141 19283102 1", status: "Picking", eta: "07:10" },
    ],
    year: 2026,
    period: 8,
    weekInYear: 29,
  },
  {
    id: "406182407",
    region: "Northcentral",
    market: "Upper Midwest",
    location: "Sioux Falls SD",
    locationType: "Warehouse",
    driver: "Michael Okafor",
    status: "Complete",
    onTime: true,
    arrivedAsExpected: "Y",
    manifestHour: 2,
    deliveryDate: "2026-08-12",
    localRouteId: "40231-01",
    nationalRouteId: "NR-620693",
    manifestArrivalDateTime: "2026-08-12 02:15",
    actualArrivalDateTime: "2026-08-12 02:04",
    actualCompletedDateTime: "2026-08-12 02:41",
    percentComplete: 100,
    vehicle: "PV-20417",
    orderCount: 18,
    ssccDetails: [],
    year: 2026,
    period: 8,
    weekInYear: 29,
  },
  {
    id: "406182450",
    region: "Northcentral",
    market: "Upper Midwest",
    location: "Sioux Falls SD",
    locationType: "Warehouse",
    driver: "Teresa Reyes",
    status: "Complete",
    onTime: true,
    arrivedAsExpected: "Y",
    manifestHour: 2,
    deliveryDate: "2026-08-12",
    localRouteId: "40233-02",
    nationalRouteId: "NR-620699",
    manifestArrivalDateTime: "2026-08-12 02:45",
    actualArrivalDateTime: "2026-08-12 02:51",
    actualCompletedDateTime: "2026-08-12 03:12",
    percentComplete: 100,
    vehicle: "PV-20433",
    orderCount: 22,
    ssccDetails: [],
    year: 2026,
    period: 8,
    weekInYear: 29,
  },
  {
    id: "406182535",
    region: "Northcentral",
    market: "Upper Midwest",
    location: "Minneapolis MN",
    locationType: "Warehouse",
    driver: "Anders Lindqvist",
    status: "Complete",
    onTime: false,
    arrivedAsExpected: "Y",
    manifestHour: 3,
    deliveryDate: "2026-08-12",
    localRouteId: "40510-01",
    nationalRouteId: "NR-621530",
    manifestArrivalDateTime: "2026-08-12 03:00",
    actualArrivalDateTime: "2026-08-12 02:55",
    actualCompletedDateTime: "2026-08-12 04:22",
    percentComplete: 100,
    vehicle: "PV-21108",
    orderCount: 25,
    ssccDetails: [],
    year: 2026,
    period: 8,
    weekInYear: 29,
  },
  {
    id: "406182571",
    region: "Northcentral",
    market: "Great Lakes",
    location: "Chicago IL",
    locationType: "Warehouse",
    driver: "Daniel Kowalski",
    status: "Incomplete",
    onTime: false,
    arrivedAsExpected: "Y",
    manifestHour: 3,
    deliveryDate: "2026-08-12",
    localRouteId: "40818-03",
    nationalRouteId: "NR-622454",
    manifestArrivalDateTime: "2026-08-12 03:15",
    actualArrivalDateTime: "2026-08-12 03:09",
    actualCompletedDateTime: "",
    percentComplete: 68,
    vehicle: "PV-21566",
    orderCount: 31,
    ssccDetails: [
      {
        stop: 4,
        ssccId: "(00) 1 0614141 19283746 5",
        eta: "07:40",
        status: "Not Loaded",
      },
      {
        stop: 7,
        ssccId: "(00) 1 0614141 19283747 2",
        eta: "07:55",
        status: "Not Loaded",
      },
      {
        stop: 9,
        ssccId: "(00) 1 0614141 19283751 9",
        eta: "08:10",
        status: "Picking",
      },
    ],
    year: 2026,
    period: 8,
    weekInYear: 29,
  },
  {
    id: "406182729",
    region: "West",
    market: "Southern California",
    location: "Los Angeles CA",
    locationType: "Warehouse",
    driver: "Kenji Nakamura",
    status: "Incomplete",
    onTime: false,
    arrivedAsExpected: "Y",
    manifestHour: 4,
    deliveryDate: "2026-08-12",
    localRouteId: "41507-04",
    nationalRouteId: "NR-624521",
    manifestArrivalDateTime: "2026-08-12 04:15",
    actualArrivalDateTime: "2026-08-12 04:31",
    actualCompletedDateTime: "",
    percentComplete: 42,
    vehicle: "PV-23055",
    orderCount: 36,
    ssccDetails: [
      {
        stop: 2,
        ssccId: "(00) 1 0614141 19284002 8",
        eta: "08:25",
        status: "Not Loaded",
      },
      {
        stop: 5,
        ssccId: "(00) 1 0614141 19284006 6",
        eta: "08:40",
        status: "Not Loaded",
      },
    ],
    year: 2026,
    period: 8,
    weekInYear: 29,
  },
  {
    id: "406182650",
    region: "Southeast",
    market: "Georgia-Alabama",
    location: "Atlanta GA",
    locationType: "Warehouse",
    driver: "Javier Castillo",
    status: "Complete",
    onTime: false,
    arrivedAsExpected: "Blank",
    manifestHour: 4,
    deliveryDate: "2026-08-12",
    localRouteId: "41244-02",
    nationalRouteId: "NR-623732",
    manifestArrivalDateTime: "",
    actualArrivalDateTime: "",
    actualCompletedDateTime: "2026-08-12 04:28",
    percentComplete: 100,
    vehicle: "PV-22241",
    orderCount: 17,
    ssccDetails: [],
    year: 2026,
    period: 8,
    weekInYear: 29,
  },
];

const LOAD_STATUS_OPTIONS = ["Complete", "Incomplete"] as const;
const LOAD_ON_TIME_OPTIONS = ["On Time", "Late"] as const;
const LOAD_AAE_OPTIONS = ["Y", "N", "Blank"] as const;

export interface AppliedFiltersSnapshot {
  selectedRegions: KPIFilterDropdownItem[];
  selectedMarkets: KPIFilterDropdownItem[];
  selectedLocations: KPIFilterDropdownItem[];
  selectedLocationTypes: KPIFilterDropdownItem[];
  selectedYears: KPIFilterDropdownItem[];
  selectedPeriods: KPIFilterDropdownItem[];
  selectedWeeks: KPIFilterDropdownItem[];
  deliveryDateRange: DeliveryDateRange;
  activeQuickSelect: QuickSelectKey | null;
  selectedDrivers: KPIFilterDropdownItem[];
  selectedStatuses: KPIFilterDropdownItem[];
  selectedOnTime: KPIFilterDropdownItem[];
  selectedArrivedAsExpected: KPIFilterDropdownItem[];
  manifestHourRange: { from: number; to: number };
  loadSearch: string;
}

export const normalizeFilterValue = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

export const getRegionLabel = (loc: any): string =>
  String(loc?.region ?? loc?.unit ?? "").trim();

export const getMarketLabel = (loc: any): string =>
  String(
    loc?.market ?? loc?.pod ?? loc?.marketPod ?? loc?.marketName ?? "",
  ).trim();

export const getLocationLabel = (loc: any): string =>
  String(
    loc?.locName ??
      loc?.locationName ??
      loc?.name ??
      loc?.locId ??
      loc?.locationId ??
      "",
  ).trim();

export const getLocationTypeLabel = (loc: any): string => {
  const explicitType = String(
    loc?.locationType ?? loc?.locType ?? loc?.type ?? "",
  ).trim();

  if (explicitType) {
    return explicitType;
  }

  const locKey = getLocationLabel(loc) || String(loc?.locId ?? "");
  return deterministicBetween(`${locKey}-location-type`, 0, 1) > 0.72
    ? "Satellite"
    : "Warehouse";
};

const asPeriodNumber = (id: string | number): number => {
  const parsed = Number(String(id).replace(/^P/i, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const fmtCount = (value: number): string => value.toLocaleString("en-US");

export const toISODate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const stableHash = (input: string): number => {
  let hash = 0;
  for (let idx = 0; idx < input.length; idx++) {
    hash = (hash * 31 + input.charCodeAt(idx)) >>> 0;
  }
  return hash;
};

export const deterministicBetween = (
  key: string,
  min: number,
  max: number,
): number => {
  const h = stableHash(key) % 10000;
  const ratio = h / 10000;
  return min + (max - min) * ratio;
};

const OnTimeDispatchDashboard: React.FC = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { width } = useWindowDimensions();
  const isMobileIos = Platform.OS === "ios" && width < 768;
  // iPad: iOS native with width >= 768 (covers both portrait and landscape)
  const isTabletIpad = Platform.OS === "ios" && width >= 768;
  const isDarkTablet = isTabletIpad && isDark;

  // Phase 4: Toast for reset notification
  const { showToast } = useToast();

  const reportConfig = useSelector((state: RootState) =>
    state.reportingConfiguration.rows.find((row) => row.link === REPORT_LINK),
  );
  const title = reportConfig?.name || "On-Time Dispatch";
  const iconName = getValidIconName(reportConfig?.icon || "local-shipping");

  const currentPeriodWeek = useSelector(
    (state: RootState) => state.appData?.periodWeek?.data,
  );
  const headerSelectedLocation = useSelector(
    (state: RootState) => state.appData.selectedLocation,
  );

  const initialLocationFromHeader = useMemo<KPIFilterDropdownItem[]>(() => {
    if (!headerSelectedLocation) return [];
    const locId = String(
      headerSelectedLocation.id || headerSelectedLocation.locationId || "",
    );
    const locName = String(
      headerSelectedLocation.displayText || headerSelectedLocation.name || "",
    ).trim();
    if (!locId && !locName) return [];
    return [{ id: locId || locName, displayText: locName || locId }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const [activeTab, setActiveTab] =
    useState<OnTimeDispatchTab>("executive-overview");
  const [locationTableColumnConfig, setLocationTableColumnConfig] = useState(
    DEFAULT_LOCATION_TABLE_COLUMN_CONFIG,
  );
  const [locationTableColumnSectionState, setLocationTableColumnSectionState] =
    useState(DEFAULT_LOCATION_TABLE_COLUMN_SECTION_STATE);
  const [openFilterKey, setOpenFilterKey] = useState<FilterKey | null>(null);
  const [activeQuickSelect, setActiveQuickSelect] =
    useState<QuickSelectKey | null>(null);

  const [selectedRegions, setSelectedRegions] = useState<
    KPIFilterDropdownItem[]
  >([]);
  const [selectedMarkets, setSelectedMarkets] = useState<
    KPIFilterDropdownItem[]
  >([]);
  const [selectedLocations, setSelectedLocations] = useState<
    KPIFilterDropdownItem[]
    // ✅ FIX: Do NOT include initialLocationFromHeader by default
    // This was causing only one location ID to be sent on initial load
  >([]);
  const [selectedLocationTypes, setSelectedLocationTypes] = useState<
    KPIFilterDropdownItem[]
  >([]);
  const [selectedYears, setSelectedYears] = useState<KPIFilterDropdownItem[]>([
    { id: String(currentYear), displayText: String(currentYear) },
  ]);
  const [selectedPeriods, setSelectedPeriods] = useState<
    KPIFilterDropdownItem[]
  >([]);
  const [selectedWeeks, setSelectedWeeks] = useState<KPIFilterDropdownItem[]>(
    [],
  );
  const [deliveryDateRange, setDeliveryDateRange] = useState<DeliveryDateRange>(
    { start: null, end: null },
  );
  const [selectedDrivers, setSelectedDrivers] = useState<
    KPIFilterDropdownItem[]
  >([]);
  const [selectedStatuses, setSelectedStatuses] = useState<
    KPIFilterDropdownItem[]
  >([]);
  const [selectedOnTime, setSelectedOnTime] = useState<KPIFilterDropdownItem[]>(
    [],
  );
  const [selectedArrivedAsExpected, setSelectedArrivedAsExpected] = useState<
    KPIFilterDropdownItem[]
  >([]);
  const [manifestHourFrom, setManifestHourFrom] = useState<number>(0);
  const [manifestHourTo, setManifestHourTo] = useState<number>(23);
  const [loadSearch, setLoadSearch] = useState<string>("");

  const [appliedFilters, setAppliedFilters] = useState<AppliedFiltersSnapshot>({
    selectedRegions: [],
    selectedMarkets: [],
    // ✅ FIX: Do NOT include initialLocationFromHeader by default
    // This was causing only one location ID to be sent on initial load
    // When no location is explicitly selected, ALL applicable locations should be included
    selectedLocations: [],
    selectedLocationTypes: [],
    selectedYears: [
      { id: String(currentYear), displayText: String(currentYear) },
    ],
    selectedPeriods: [],
    selectedWeeks: [],
    deliveryDateRange: { start: null, end: null },
    activeQuickSelect: null,
    selectedDrivers: [],
    selectedStatuses: [],
    selectedOnTime: [],
    selectedArrivedAsExpected: [],
    manifestHourRange: { from: 0, to: 23 },
    loadSearch: "",
  });

  const [periodWeekData, setPeriodWeekData] =
    useState<PeriodWeekResponse | null>(null);

  // Location dropdown data now comes directly from the access-scoped
  // Redux state populated once at app init via GET /common/location-with-access
  // (see src/store/slices/appDataSlice.ts / src/components/AppWrapper.tsx),
  // instead of the separate unscoped /admin/locations list. The identifier
  // name `adminLocations` is kept so every existing downstream consumer in
  // this file (cascading filters, mock data, service hooks) needs no changes.
  // Explicit return-type annotation keeps `adminLocations` (and everything
  // derived from it below) soundly typed as `EnrichedLocation[]` even though
  // `state.appData` itself is currently typed as `unknown` in RootState
  // (a pre-existing, unrelated store-typing gap).
  const adminLocations = useSelector(
    (state: RootState): EnrichedLocation[] => state.appData.locations,
  );
  const { latestUpdateDisplayValue } = useOnTimeDispatchLatestUpdate();

  const { kpiData, kpiLoading, kpiError, refetchKpi } = useOnTimeDispatchKpi(
    appliedFilters,
    activeTab === "executive-overview",
    adminLocations,
  );

  // ✅ FIX: Convert filter IDs to strings to ensure type compatibility with API
  // KPIFilterDropdownItem.id can be string | number, but API expects string
  const normalizedAppliedFilters =
    useMemo<AppliedFiltersForLoadExecution>(() => {
      return {
        selectedRegions: appliedFilters.selectedRegions.map((r) => ({
          id: String(r.id),
          displayText: r.displayText,
        })),
        selectedMarkets: appliedFilters.selectedMarkets.map((m) => ({
          id: String(m.id),
          displayText: m.displayText,
        })),
        selectedLocations: appliedFilters.selectedLocations.map((l) => ({
          id: String(l.id),
          displayText: l.displayText,
        })),
        selectedLocationTypes: appliedFilters.selectedLocationTypes.map(
          (lt) => ({
            id: String(lt.id),
            displayText: lt.displayText,
          }),
        ),
        selectedYears: appliedFilters.selectedYears.map((y) => ({
          id: String(y.id),
          displayText: y.displayText,
        })),
        selectedPeriods: appliedFilters.selectedPeriods.map((p) => ({
          id: String(p.id),
          displayText: p.displayText,
        })),
        selectedWeeks: appliedFilters.selectedWeeks.map((w) => ({
          id: String(w.id),
          displayText: w.displayText,
        })),
        deliveryDateRange: appliedFilters.deliveryDateRange,
        selectedDrivers: appliedFilters.selectedDrivers.map((d) => ({
          id: String(d.id),
          displayText: d.displayText,
        })),
        selectedStatuses: appliedFilters.selectedStatuses.map((s) => ({
          id: String(s.id),
          displayText: s.displayText,
        })),
        selectedOnTime: appliedFilters.selectedOnTime.map((ot) => ({
          id: String(ot.id),
          displayText: ot.displayText,
        })),
        selectedArrivedAsExpected: appliedFilters.selectedArrivedAsExpected.map(
          (aae) => ({
            id: String(aae.id),
            displayText: aae.displayText,
          }),
        ),
        manifestHourRange: appliedFilters.manifestHourRange,
        search: appliedFilters.loadSearch.trim() || undefined,
      };
    }, [appliedFilters]);

  const {
    scorecardData,
    scorecardLoading,
    scorecardError,
    resetScorecardsData,
  } = useLoadExecutionScorecards(
    normalizedAppliedFilters,
    activeTab === "load-execution",
    adminLocations,
  );

  const {
    records: detailRecords,
    loading: detailLoading,
    error: detailError,
    totalElements,
    pageNumber,
    pageSize,
    sortBy,
    sortDirection,
    setPageNumber,
    setPageSize,
    setSorting,
    refresh: refreshDetail,
  } = useLoadExecutionDetail(
    normalizedAppliedFilters,
    activeTab === "load-execution",
    adminLocations,
  );

  // ─── Cascading Filter Logic: Region → Market → Location → LocationType ───────────────

  // Normalized region IDs for filtering (empty array means "all")
  const selectedRegionIds = useMemo(
    () => selectedRegions.map((r) => normalizeFilterValue(r.id)),
    [selectedRegions],
  );

  // Normalized market IDs for filtering (empty array means "all")
  const selectedMarketIds = useMemo(
    () => selectedMarkets.map((m) => normalizeFilterValue(m.id)),
    [selectedMarkets],
  );

  // Market options filtered by selected regions
  // If no regions selected, show all markets from all locations
  // If regions selected, show markets that have locations in those regions
  const regionFilteredMarkets: KPIFilterDropdownItem[] = useMemo(() => {
    if (selectedRegionIds.length === 0) {
      // No regions selected = show all unique markets
      const byNormalizedValue = new Map<string, string>();
      for (const loc of adminLocations) {
        const marketLabel = getMarketLabel(loc);
        const normalized = normalizeFilterValue(marketLabel);
        if (normalized && !byNormalizedValue.has(normalized)) {
          byNormalizedValue.set(normalized, marketLabel);
        }
      }
      return Array.from(byNormalizedValue.entries())
        .map(([id, displayText]) => ({ id, displayText }))
        .sort((a, b) => a.displayText.localeCompare(b.displayText));
    }

    // Regions selected = show markets that exist in those regions
    const byNormalizedValue = new Map<string, string>();
    for (const loc of adminLocations) {
      const regionLabel = getRegionLabel(loc);
      const normalizedRegion = normalizeFilterValue(regionLabel);
      // Only include markets from selected regions
      if (selectedRegionIds.includes(normalizedRegion)) {
        const marketLabel = getMarketLabel(loc);
        const normalizedMarket = normalizeFilterValue(marketLabel);
        if (normalizedMarket && !byNormalizedValue.has(normalizedMarket)) {
          byNormalizedValue.set(normalizedMarket, marketLabel);
        }
      }
    }
    return Array.from(byNormalizedValue.entries())
      .map(([id, displayText]) => ({ id, displayText }))
      .sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [adminLocations, selectedRegionIds]);

  // Auto-clear selectedMarkets when regionFilteredMarkets changes
  // (markets may become invalid when region selection changes)
  useEffect(() => {
    if (selectedMarkets.length === 0) return;
    const validMarkets = selectedMarkets.filter((m) =>
      regionFilteredMarkets.some(
        (opt) => normalizeFilterValue(opt.id) === normalizeFilterValue(m.id),
      ),
    );
    if (validMarkets.length !== selectedMarkets.length) {
      setSelectedMarkets(validMarkets);
    }
  }, [regionFilteredMarkets]);

  // Locations filtered by Region + Market
  const regionMarketFilteredLocations = useMemo(() => {
    return adminLocations.filter((loc) => {
      const locRegion = normalizeFilterValue(getRegionLabel(loc));
      const locMarket = normalizeFilterValue(getMarketLabel(loc));

      // Check region match (if regions selected)
      if (selectedRegionIds.length > 0) {
        if (!selectedRegionIds.includes(locRegion)) return false;
      }

      // Check market match (if markets selected)
      if (selectedMarketIds.length > 0) {
        if (!selectedMarketIds.includes(locMarket)) return false;
      }

      return true;
    });
  }, [adminLocations, selectedRegionIds, selectedMarketIds]);

  // Location Type options derived from filtered locations
  const locationTypeOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const byNormalizedValue = new Map<string, string>();

    for (const loc of regionMarketFilteredLocations) {
      const rawType = String(loc.locationType ?? "").trim();
      const normalized = normalizeFilterValue(rawType) || "unknown";
      const displayText = normalized === "unknown" ? "Unknown" : rawType;

      if (!byNormalizedValue.has(normalized)) {
        byNormalizedValue.set(normalized, displayText);
      }
    }

    return Array.from(byNormalizedValue.entries())
      .map(([id, displayText]) => ({ id, displayText }))
      .sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [regionMarketFilteredLocations]);

  // Normalized location type IDs for filtering
  const selectedLocationTypeIds = useMemo(
    () => selectedLocationTypes.map((lt) => normalizeFilterValue(lt.id)),
    [selectedLocationTypes],
  );

  // Auto-clear selectedLocationTypes when locationTypeOptions changes
  useEffect(() => {
    if (selectedLocationTypes.length === 0) return;
    const validTypes = selectedLocationTypes.filter((lt) =>
      locationTypeOptions.some(
        (opt) => normalizeFilterValue(opt.id) === normalizeFilterValue(lt.id),
      ),
    );
    if (validTypes.length !== selectedLocationTypes.length) {
      setSelectedLocationTypes(validTypes);
    }
  }, [locationTypeOptions]);

  // Final Location options: filtered by Region + Market + LocationType
  const locationOptions: KPIFilterDropdownItem[] = useMemo(() => {
    return regionMarketFilteredLocations
      .filter((loc) => {
        // Skip entries without a resolvable location id — nothing to filter by.
        if (loc.locationId === null || loc.locationId === undefined) {
          return false;
        }
        // If location types selected, filter by type
        if (selectedLocationTypeIds.length > 0) {
          const locType = normalizeFilterValue(loc.locationType ?? "");
          const normalizedType = locType || "unknown";
          if (!selectedLocationTypeIds.includes(normalizedType)) {
            return false;
          }
        }
        return true;
      })
      .map((loc) => ({
        id: String(loc.locationId),
        displayText: getLocationLabel(loc) || `Location ${loc.locationId}`,
      }))
      .sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [regionMarketFilteredLocations, selectedLocationTypeIds]);

  // Auto-clear selectedLocations when locationOptions changes
  useEffect(() => {
    if (selectedLocations.length === 0) return;
    const validLocations = selectedLocations.filter((loc) =>
      locationOptions.some(
        (opt) => normalizeFilterValue(opt.id) === normalizeFilterValue(loc.id),
      ),
    );
    if (validLocations.length !== selectedLocations.length) {
      setSelectedLocations(validLocations);
    }
  }, [locationOptions]);

  // ─── End Cascading Logic ─────────────────────────────────────────────────────────

  type LoadExecutionGeneratedRow = LoadExecutionRow & {
    year: number;
    period: number;
    weekInYear: number;
  };

  const mockLoadExecutionRows = useMemo<LoadExecutionGeneratedRow[]>(() => {
    if (!IS_LOAD_EXECUTION_ENABLED || activeTab !== "load-execution") {
      return [];
    }

    const source = (adminLocations.length ? adminLocations : []).slice(0, 120);
    const years = [currentYear - 1, currentYear];
    const out: LoadExecutionGeneratedRow[] = [...LOAD_EXECUTION_SEED_ROWS];

    source.forEach((loc, locIndex) => {
      const region = getRegionLabel(loc) || "Unknown Region";
      const market = getMarketLabel(loc) || "Unknown Market / POD";
      const location = getLocationLabel(loc) || `Location ${locIndex + 1}`;
      const locationType = getLocationTypeLabel(loc);
      const loadCount = Math.max(
        2,
        Math.round(deterministicBetween(`${location}-load-count`, 2, 5)),
      );

      for (let i = 0; i < loadCount; i++) {
        const loadId = `LD-${String(locIndex + 1).padStart(3, "0")}-${String(
          i + 1,
        ).padStart(2, "0")}`;
        if (LOAD_EXECUTION_SEED_ROWS.some((row) => row.id === loadId)) {
          continue;
        }
        const driver = `Driver ${String(
          Math.round(deterministicBetween(`${loadId}-driver`, 1, 34)),
        ).padStart(2, "0")}`;
        const manifestHour = Math.round(
          deterministicBetween(`${loadId}-hour`, 0, 23),
        );
        const status: "Complete" | "Incomplete" =
          deterministicBetween(`${loadId}-status`, 0, 1) > 0.16
            ? "Complete"
            : "Incomplete";
        const onTime = deterministicBetween(`${loadId}-ontime`, 0, 1) > 0.18;
        const aaeSeed = deterministicBetween(`${loadId}-aae`, 0, 1);
        const arrivedAsExpected: "Y" | "N" | "Blank" =
          aaeSeed > 0.32 ? "Y" : aaeSeed > 0.14 ? "N" : "Blank";

        const plannedLoads = Math.round(
          deterministicBetween(`${loadId}-planned`, 8, 24),
        );
        const incompleteLoads =
          status === "Incomplete"
            ? Math.round(deterministicBetween(`${loadId}-inc`, 1, 4))
            : 0;
        const completedLoads = Math.max(0, plannedLoads - incompleteLoads);
        const onTimeLoads = Math.max(
          0,
          completedLoads -
            Math.round(deterministicBetween(`${loadId}-late`, 0, 3)),
        );
        const percentComplete = plannedLoads
          ? (completedLoads / plannedLoads) * 100
          : 0;

        const year =
          years[
            Math.round(
              deterministicBetween(`${loadId}-year`, 0, years.length - 1),
            )
          ] || currentYear;
        const period = Math.round(
          deterministicBetween(`${loadId}-period`, 1, 13),
        );
        const weekInPeriod = Math.round(
          deterministicBetween(`${loadId}-weekinperiod`, 1, 4),
        );
        const weekInYear = (period - 1) * WEEKS_PER_PERIOD + weekInPeriod;
        const deliveryDate = `${year}-${String(
          Math.max(1, Math.min(12, period)),
        ).padStart(2, "0")}-${String(
          Math.max(1, Math.min(28, weekInPeriod * 7)),
        ).padStart(2, "0")}`;
        const manifestArrivalDateTime = `${deliveryDate} ${String(
          manifestHour,
        ).padStart(2, "0")}:15`;
        const arrivalOffset = Math.round(
          deterministicBetween(`${loadId}-arrival-offset`, -18, 24),
        );
        const arrivalHour = Math.max(
          0,
          Math.min(23, manifestHour + (arrivalOffset >= 0 ? 0 : -1)),
        );
        const arrivalMinute = Math.max(0, Math.min(59, 15 + arrivalOffset));
        const actualArrivalDateTime =
          arrivedAsExpected === "Blank"
            ? ""
            : `${deliveryDate} ${String(arrivalHour).padStart(2, "0")}:${String(
                arrivalMinute,
              ).padStart(2, "0")}`;
        const actualCompletedDateTime =
          status === "Incomplete"
            ? ""
            : `${deliveryDate} ${String(
                Math.max(0, Math.min(23, manifestHour + 1)),
              ).padStart(2, "0")}:${String(
                Math.round(
                  deterministicBetween(`${loadId}-done-minute`, 5, 55),
                ),
              ).padStart(2, "0")}`;
        const localRouteId = `${String(
          Math.round(deterministicBetween(`${loadId}-route`, 40000, 42999)),
        )}-${String(
          Math.round(deterministicBetween(`${loadId}-route-seq`, 1, 4)),
        ).padStart(2, "0")}`;
        const nationalRouteId = `NR-${String(
          Math.round(deterministicBetween(`${loadId}-nroute`, 620000, 626999)),
        )}`;
        const vehicle = `PV-${String(
          Math.round(deterministicBetween(`${loadId}-vehicle`, 20000, 24999)),
        )}`;
        const orderCount = Math.round(
          deterministicBetween(`${loadId}-orders`, 12, 36),
        );

        const ssccCount =
          status === "Incomplete"
            ? Math.max(
                1,
                Math.round(deterministicBetween(`${loadId}-sscc`, 1, 4)),
              )
            : Math.round(deterministicBetween(`${loadId}-sscc`, 1, 2));
        const ssccDetails = Array.from(
          { length: ssccCount },
          (_, ssccIndex) => {
            const ssccStatus =
              deterministicBetween(
                `${loadId}-sscc-${ssccIndex}-status`,
                0,
                1,
              ) > 0.35
                ? "Not Loaded"
                : "Picking";
            return {
              stop: ssccIndex === 0 ? Math.round(deterministicBetween(`${loadId}-sscc-stop-1`, 1, 3)) : (ssccIndex + 1) * 2 + 1,
              ssccId: `(00) 1 0614141 ${String(
                Math.round(
                  deterministicBetween(
                    `${loadId}-sscc-${ssccIndex}-barcode`,
                    19280000,
                    19289999,
                  ),
                ),
              )} ${String(ssccIndex + 1)}`,
              status: ssccStatus,
              eta: `${String(
                Math.max(0, Math.min(23, manifestHour + 4)),
              ).padStart(2, "0")}:${String(10 + ssccIndex * 15).padStart(
                2,
                "0",
              )}`,
            };
          },
        );

        out.push({
          id: loadId,
          region,
          market,
          location,
          locationType,
          driver,
          status,
          onTime,
          arrivedAsExpected,
          manifestHour,
          deliveryDate,
          localRouteId,
          nationalRouteId,
          manifestArrivalDateTime,
          actualArrivalDateTime,
          actualCompletedDateTime,
          percentComplete,
          vehicle,
          orderCount,
          ssccDetails,
          year,
          period,
          weekInYear,
        });
      }
    });

    return out;
  }, [activeTab, adminLocations, currentYear]);

  // const driverOptions = useMemo<KPIFilterDropdownItem[]>(() => {
  //   return Array.from(new Set(mockLoadExecutionRows.map((row) => row.driver)))
  //     .sort((a, b) => a.localeCompare(b))
  //     .map((driver) => ({ id: driver, displayText: driver }));
  // }, [mockLoadExecutionRows]);
  const driverOptions = useMemo<KPIFilterDropdownItem[]>(() => {
  return Array.from(
    new Set((detailRecords ?? []).map((record) => String(record.driver ?? "").trim()).filter(Boolean))
  )
    .sort((a, b) => a.localeCompare(b))
    .map((driver) => ({ id: driver, displayText: driver }));
}, [detailRecords]);

  const loadStatusOptions = useMemo<KPIFilterDropdownItem[]>(() => {
    return LOAD_STATUS_OPTIONS.map((item) => ({ id: item, displayText: item }));
  }, []);

  const loadOnTimeOptions = useMemo<KPIFilterDropdownItem[]>(() => {
    return LOAD_ON_TIME_OPTIONS.map((item) => ({
      id: item,
      displayText: item,
    }));
  }, []);

  const loadArrivedExpectedOptions = useMemo<KPIFilterDropdownItem[]>(() => {
    return LOAD_AAE_OPTIONS.map((item) => ({ id: item, displayText: item }));
  }, []);

  const manifestHourOptions = useMemo(() => {
    return Array.from({ length: 24 }, (_, hour) => ({
      id: String(hour),
      displayText: String(hour).padStart(2, "0"),
    }));
  }, []);

  const manifestHourFromSelection = useMemo<KPIFilterDropdownItem[]>(
    () => [
      {
        id: String(manifestHourFrom),
        displayText: String(manifestHourFrom).padStart(2, "0"),
      },
    ],
    [manifestHourFrom],
  );

  const manifestHourToSelection = useMemo<KPIFilterDropdownItem[]>(
    () => [
      {
        id: String(manifestHourTo),
        displayText: String(manifestHourTo).padStart(2, "0"),
      },
    ],
    [manifestHourTo],
  );

  const filteredLoadExecutionRows = useMemo<LoadExecutionRow[]>(() => {
    if (!IS_LOAD_EXECUTION_ENABLED || activeTab !== "load-execution") {
      return [];
    }

    const regionSet = new Set(
      appliedFilters.selectedRegions.map((item) =>
        normalizeFilterValue(item.id),
      ),
    );
    const marketSet = new Set(
      appliedFilters.selectedMarkets.map((item) =>
        normalizeFilterValue(item.id),
      ),
    );
    const locationSet = new Set(
      appliedFilters.selectedLocations.flatMap((item) => [
        normalizeFilterValue(item.id),
        normalizeFilterValue(item.displayText),
      ]),
    );
    const locationTypeSet = new Set(
      appliedFilters.selectedLocationTypes.map((item) =>
        normalizeFilterValue(item.id),
      ),
    );
    const yearSet = new Set(
      appliedFilters.selectedYears
        .map((item) => Number(item.id))
        .filter((n) => Number.isFinite(n)),
    );
    const periodSet = new Set(
      appliedFilters.selectedPeriods
        .map((item) => asPeriodNumber(item.id))
        .filter((n) => n > 0),
    );
    const weekSet = new Set(
      appliedFilters.selectedWeeks
        .map((item) => Number(item.id))
        .filter((n) => Number.isFinite(n)),
    );

    const driverSet = new Set(
      appliedFilters.selectedDrivers.map((item) =>
        normalizeFilterValue(item.id),
      ),
    );
    const statusSet = new Set(
      appliedFilters.selectedStatuses.map((item) =>
        normalizeFilterValue(item.id),
      ),
    );
    const onTimeSet = new Set(
      appliedFilters.selectedOnTime.map((item) =>
        normalizeFilterValue(item.id),
      ),
    );
    const aaeSet = new Set(
      appliedFilters.selectedArrivedAsExpected.map((item) =>
        normalizeFilterValue(item.id),
      ),
    );
    const searchTerm = normalizeFilterValue(appliedFilters.loadSearch);

    const selectedDate =
      appliedFilters.deliveryDateRange.end ||
      appliedFilters.deliveryDateRange.start;

    return mockLoadExecutionRows.filter((row) => {
      if (
        regionSet.size > 0 &&
        !regionSet.has(normalizeFilterValue(row.region))
      )
        return false;
      if (
        marketSet.size > 0 &&
        !marketSet.has(normalizeFilterValue(row.market))
      )
        return false;
      if (
        locationSet.size > 0 &&
        !locationSet.has(normalizeFilterValue(row.location))
      )
        return false;
      if (
        locationTypeSet.size > 0 &&
        !locationTypeSet.has(normalizeFilterValue(row.locationType))
      )
        return false;
      if (yearSet.size > 0 && !yearSet.has(row.year)) return false;
      if (periodSet.size > 0 && !periodSet.has(row.period)) return false;
      if (weekSet.size > 0 && !weekSet.has(row.weekInYear)) return false;
      if (selectedDate && row.deliveryDate !== selectedDate) return false;

      if (
        driverSet.size > 0 &&
        !driverSet.has(normalizeFilterValue(row.driver))
      )
        return false;
      if (
        statusSet.size > 0 &&
        !statusSet.has(normalizeFilterValue(row.status))
      )
        return false;

      const onTimeLabel = row.onTime ? "on time" : "late";
      if (onTimeSet.size > 0 && !onTimeSet.has(onTimeLabel)) return false;
      if (
        aaeSet.size > 0 &&
        !aaeSet.has(normalizeFilterValue(row.arrivedAsExpected))
      )
        return false;

      const fromHour = Math.min(
        appliedFilters.manifestHourRange.from,
        appliedFilters.manifestHourRange.to,
      );
      const toHour = Math.max(
        appliedFilters.manifestHourRange.from,
        appliedFilters.manifestHourRange.to,
      );
      if (row.manifestHour < fromHour || row.manifestHour > toHour)
        return false;

      if (searchTerm) {
        const haystack = normalizeFilterValue(
          `${row.id} ${row.driver} ${row.region} ${row.market} ${row.location} ${row.status} ${row.deliveryDate} ${row.localRouteId} ${row.nationalRouteId}`,
        );
        if (!haystack.includes(searchTerm)) return false;
      }

      return true;
    });
    // Note: no separate "incomplete focused" flag is needed here — the
    // Incomplete Loads KPI card drill-down applies status: ["Incomplete"] to
    // appliedFilters.selectedStatuses, which the statusSet check above
    // already enforces.
  }, [activeTab, appliedFilters, mockLoadExecutionRows]);

  const regionOptions = useMemo<KPIFilterDropdownItem[]>(() => {
    const seen = new Set<string>();
    const out: KPIFilterDropdownItem[] = [];
    adminLocations.forEach((loc) => {
      const region = getRegionLabel(loc);
      if (!region || seen.has(region)) return;
      seen.add(region);
      out.push({ id: region, displayText: region });
    });
    return out.sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [adminLocations]);

  // NOTE: marketOptions, locationOptions, and locationTypeOptions are now defined
  // in the Cascading Filter Logic section above. They provide proper region/market/type
  // cascading with auto-clearing of invalid selections.

  const yearOptions = useMemo<KPIFilterDropdownItem[]>(() => {
    return Array.from({ length: 2 }, (_, i) => {
      const year = String(currentYear - i);
      return { id: year, displayText: year };
    });
  }, [currentYear]);

  const selectedYearNumbers = useMemo(() => {
    return selectedYears
      .map((y) => Number(y.id))
      .filter((n) => Number.isFinite(n));
  }, [selectedYears]);

  useEffect(() => {
    if (selectedYearNumbers.length === 0) {
      setPeriodWeekData(null);
      return;
    }

    let isMounted = true;
    fetchPeriodWeeks(selectedYearNumbers)
      .then((res) => {
        if (!isMounted) return;
        setPeriodWeekData(res);
      })
      .catch(() => {
        if (!isMounted) return;
        setPeriodWeekData(null);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedYearNumbers]);

  const periodOptions = useMemo<KPIFilterDropdownItem[]>(() => {
    const set = new Set<number>();
    (periodWeekData?.data ?? []).forEach((yearItem) => {
      yearItem.periods.forEach((period) => set.add(period.period));
    });
    return Array.from(set)
      .sort((a, b) => a - b)
      .map((period) => ({ id: `P${period}`, displayText: `P${period}` }));
  }, [periodWeekData]);

  // Auto-clear selectedPeriods when periodOptions changes (invalid periods removed from options)
  useEffect(() => {
    if (selectedPeriods.length === 0) return;
    const validPeriods = selectedPeriods.filter((p) =>
      periodOptions.some((opt) => String(opt.id) === String(p.id)),
    );
    if (validPeriods.length !== selectedPeriods.length) {
      setSelectedPeriods(validPeriods);
    }
  }, [periodOptions]);

  const weekOptions = useMemo<KPIFilterDropdownItem[]>(() => {
    const selectedPeriodNumbers = new Set(
      selectedPeriods
        .map((period) => asPeriodNumber(period.id))
        .filter((period) => period > 0),
    );
    const weeks: KPIFilterDropdownItem[] = [];

    (periodWeekData?.data ?? []).forEach((yearItem) => {
      yearItem.periods.forEach((periodItem) => {
        if (
          selectedPeriodNumbers.size > 0 &&
          !selectedPeriodNumbers.has(periodItem.period)
        ) {
          return;
        }

        periodItem.weeks.forEach((weekItem) => {
          weeks.push({
            id: String(weekItem.weekInYear),
            displayText: `W${weekItem.weekInYear} (P${periodItem.period}W${weekItem.week})`,
          });
        });
      });
    });

    return weeks.sort((a, b) => Number(a.id) - Number(b.id));
  }, [periodWeekData, selectedPeriods]);

  // Auto-clear selectedWeeks when weekOptions changes (invalid weeks removed from options)
  useEffect(() => {
    if (selectedWeeks.length === 0) return;
    const validWeeks = selectedWeeks.filter((w) =>
      weekOptions.some((opt) => String(opt.id) === String(w.id)),
    );
    if (validWeeks.length !== selectedWeeks.length) {
      setSelectedWeeks(validWeeks);
    }
  }, [weekOptions]);

  const { disabledPeriodIds, disabledWeekIds } = useDisabledPeriodWeekIds({
    currentPeriodWeek,
    selectedYearNumbers,
    periodOptions,
    weekOptions,
  });

  const draftSnapshot = useMemo<AppliedFiltersSnapshot>(
    () => ({
      selectedRegions,
      selectedMarkets,
      selectedLocations,
      selectedLocationTypes,
      selectedYears,
      selectedPeriods,
      selectedWeeks,
      deliveryDateRange,
      activeQuickSelect,
      selectedDrivers,
      selectedStatuses,
      selectedOnTime,
      selectedArrivedAsExpected,
      manifestHourRange: { from: manifestHourFrom, to: manifestHourTo },
      loadSearch,
    }),
    [
      selectedRegions,
      selectedMarkets,
      selectedLocations,
      selectedLocationTypes,
      selectedYears,
      selectedPeriods,
      selectedWeeks,
      deliveryDateRange,
      activeQuickSelect,
      selectedDrivers,
      selectedStatuses,
      selectedOnTime,
      selectedArrivedAsExpected,
      manifestHourFrom,
      manifestHourTo,
      loadSearch,
    ],
  );

  const hasPendingChanges = useMemo(
    () => JSON.stringify(draftSnapshot) !== JSON.stringify(appliedFilters),
    [draftSnapshot, appliedFilters],
  );

  // Reset button is visible only when there are active applied filters or pending changes (matches mockup)
  const hasActiveAppliedFilters = useMemo(() => {
    const f = appliedFilters;
    return (
      f.selectedRegions.length > 0 ||
      f.selectedMarkets.length > 0 ||
      f.selectedLocations.length > 0 ||
      f.selectedLocationTypes.length > 0 ||
      f.selectedPeriods.length > 0 ||
      f.selectedWeeks.length > 0 ||
      !!f.deliveryDateRange.start ||
      f.selectedDrivers.length > 0 ||
      f.selectedStatuses.length > 0 ||
      f.selectedOnTime.length > 0 ||
      f.selectedArrivedAsExpected.length > 0 ||
      f.manifestHourRange.from !== 0 ||
      f.manifestHourRange.to !== 23 ||
      !!f.loadSearch.trim()
    );
  }, [appliedFilters]);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(draftSnapshot);
  }, [draftSnapshot]);

  const handleResetFilters = useCallback(() => {
    // The refactored hooks handle data fetching automatically,
    // so we don't need to manually reset data. Just reset the filter state.

    // ✅ FIX: Do NOT include initialLocationFromHeader in reset
    // This ensures all applicable locations are sent on reset
    const resetSnapshot: AppliedFiltersSnapshot = {
      selectedRegions: [],
      selectedMarkets: [],
      selectedLocations: [],
      selectedLocationTypes: [],
      selectedYears: [
        { id: String(currentYear), displayText: String(currentYear) },
      ],
      selectedPeriods: [],
      selectedWeeks: [],
      deliveryDateRange: { start: null, end: null },
      activeQuickSelect: null,
      selectedDrivers: [],
      selectedStatuses: [],
      selectedOnTime: [],
      selectedArrivedAsExpected: [],
      manifestHourRange: { from: 0, to: 23 },
      loadSearch: "",
    };
    setSelectedRegions([]);
    setSelectedMarkets([]);
    setSelectedLocations([]);
    setSelectedLocationTypes([]);
    setSelectedYears([
      { id: String(currentYear), displayText: String(currentYear) },
    ]);
    setSelectedPeriods([]);
    setSelectedWeeks([]);
    setDeliveryDateRange({ start: null, end: null });
    setActiveQuickSelect(null);
    setSelectedDrivers([]);
    setSelectedStatuses([]);
    setSelectedOnTime([]);
    setSelectedArrivedAsExpected([]);
    setManifestHourFrom(0);
    setManifestHourTo(23);
    setLoadSearch("");
    setAppliedFilters(resetSnapshot);
  }, [currentYear, initialLocationFromHeader]);

  // ─── clearActiveQuickSelect: mutual-exclusivity guard for manual Year/Period/Week edits ──
  // Timeframe (quick-select) pre-population of Year/Period/Week (applyQuickSelect,
  // below) remains completely unchanged and does NOT call this. This guard is invoked
  // only from the user-facing Year/Period/Week dropdown handlers in the JSX below, so
  // it never fires as a side effect of a preset's own programmatic updates and cannot
  // create an update loop. Once a Timeframe preset is active, a manual Year/Period/Week
  // edit means the user switched to a custom selection — so the chip/pill is deselected,
  // while the manually chosen Year/Period/Week values are preserved untouched (this guard
  // never alters them itself).
  const clearActiveQuickSelect = useCallback(() => {
    if (activeQuickSelect) {
      setActiveQuickSelect(null);
    }
  }, [activeQuickSelect]);

  const applyQuickSelect = useCallback(
    (key: QuickSelectKey) => {
      if (
        !currentPeriodWeek?.year ||
        !currentPeriodWeek?.period ||
        !currentPeriodWeek?.week
      )
        return;

      let targetYear = currentPeriodWeek.year;
      let periods: number[] = [currentPeriodWeek.period];
      let weeks: number[] = [];

      switch (key) {
        case "wtd":
          weeks = [
            currentPeriodWeek.weekInYear ||
              (currentPeriodWeek.period - 1) * 4 + currentPeriodWeek.week,
          ];
          break;
        case "ptd": {
          const startWeekInYear =
            (currentPeriodWeek.period - 1) * WEEKS_PER_PERIOD + 1;
          const endWeekInYear =
            currentPeriodWeek.weekInYear ||
            (currentPeriodWeek.period - 1) * 4 + currentPeriodWeek.week;
          weeks = Array.from(
            { length: endWeekInYear - startWeekInYear + 1 },
            (_, idx) => startWeekInYear + idx,
          );
          break;
        }
        case "qtd": {
          const quarterStart =
            [...QUARTER_START_PERIODS]
              .reverse()
              .find((item) => item <= currentPeriodWeek.period) ?? 1;
          periods = Array.from(
            { length: currentPeriodWeek.period - quarterStart + 1 },
            (_, idx) => quarterStart + idx,
          );
          weeks = [];
          break;
        }
        case "ytd":
          periods = Array.from(
            { length: currentPeriodWeek.period },
            (_, idx) => idx + 1,
          );
          weeks = [];
          break;
        case "lcw": {
          const currentWeekInYear =
            currentPeriodWeek.weekInYear ||
            (currentPeriodWeek.period - 1) * 4 + currentPeriodWeek.week;
          const lastClosedWeek = Math.max(1, currentWeekInYear - 1);
          weeks = [lastClosedWeek];
          periods = [Math.ceil(lastClosedWeek / 4)];
          break;
        }
        case "lcp": {
          let lastClosedPeriod = currentPeriodWeek.period - 1;
          if (lastClosedPeriod < 1) {
            lastClosedPeriod = 13;
            targetYear = currentPeriodWeek.year - 1;
          }
          periods = [lastClosedPeriod];
          weeks = [];
          break;
        }
        default:
          return;
      }

      const nextYears = [
        { id: String(targetYear), displayText: String(targetYear) },
      ];
      const nextPeriods = periods.map((period) => ({
        id: `P${period}`,
        displayText: `P${period}`,
      }));
      const nextWeeks = weeks.map((week) => ({
        id: String(week),
        displayText: `W${week} (P${Math.ceil(week / 4)}W${
          ((week - 1) % 4) + 1
        })`,
      }));

      setSelectedYears(nextYears);
      setSelectedPeriods(nextPeriods);
      setSelectedWeeks(nextWeeks);

      // Timeframe presets slice data via Year/Period/Week alone — forcing an
      // exact-day Delivery Date on top of that (e.g. "today") double-filters
      // the request and hides every row not literally dated today, which is
      // wrong for a period/quarter/year "to date" rollup. Clear it instead.
      const nextDeliveryDateRange = { start: null, end: null };
      setDeliveryDateRange(nextDeliveryDateRange);
      setActiveQuickSelect(key);

      // Timeframe presets are an instant-apply control (like the Status/entity
      // quick filters elsewhere in this file) — commit straight to
      // appliedFilters so the data is sliced immediately instead of requiring
      // a separate Apply click.
      setAppliedFilters((prev) => ({
        ...prev,
        selectedYears: nextYears,
        selectedPeriods: nextPeriods,
        selectedWeeks: nextWeeks,
        deliveryDateRange: nextDeliveryDateRange,
        activeQuickSelect: key,
      }));
    },
    [currentPeriodWeek],
  );

  const handleQuickSelectPress = useCallback(
    (key: QuickSelectKey) => {
      if (activeQuickSelect === key) {
        setActiveQuickSelect(null);
        setSelectedPeriods([]);
        setSelectedWeeks([]);
        // Mirror the deselect into appliedFilters so it takes effect immediately.
        setAppliedFilters((prev) => ({
          ...prev,
          selectedPeriods: [],
          selectedWeeks: [],
          activeQuickSelect: null,
        }));
        return;
      }
      applyQuickSelect(key);
    },
    [activeQuickSelect, applyQuickSelect],
  );

  // Filter LOAD_EXECUTION_SEED_ROWS based on appliedFilters
  const filteredExecutionData = useMemo(() => {
    return LOAD_EXECUTION_SEED_ROWS.filter((row) => {
      // Region filter
      if (
        appliedFilters.selectedRegions.length > 0 &&
        !appliedFilters.selectedRegions.some(
          (r) =>
            String(r.displayText).toLowerCase() ===
            String(row.region).toLowerCase(),
        )
      ) {
        return false;
      }

      // Market filter
      if (
        appliedFilters.selectedMarkets.length > 0 &&
        !appliedFilters.selectedMarkets.some(
          (m) =>
            String(m.displayText).toLowerCase() ===
            String(row.market).toLowerCase(),
        )
      ) {
        return false;
      }

      // Location filter
      if (
        appliedFilters.selectedLocations.length > 0 &&
        !appliedFilters.selectedLocations.some(
          (l) =>
            String(l.displayText).toLowerCase() ===
            String(row.location).toLowerCase(),
        )
      ) {
        return false;
      }

      // Location Type filter
      if (
        appliedFilters.selectedLocationTypes.length > 0 &&
        !appliedFilters.selectedLocationTypes.some(
          (lt) =>
            String(lt.displayText).toLowerCase() ===
            String(row.locationType).toLowerCase(),
        )
      ) {
        return false;
      }

      // Year filter
      if (
        appliedFilters.selectedYears.length > 0 &&
        !appliedFilters.selectedYears.some((y) => Number(y.id) === row.year)
      ) {
        return false;
      }

      // Period filter
      if (
        appliedFilters.selectedPeriods.length > 0 &&
        !appliedFilters.selectedPeriods.some(
          (p) => Number(p.id.replace(/^P/i, "")) === row.period,
        )
      ) {
        return false;
      }

      // Week filter
      if (
        appliedFilters.selectedWeeks.length > 0 &&
        !appliedFilters.selectedWeeks.some((w) => {
          const match = String(w.displayText).match(/\(P(\d+)W(\d+)\)/);
          if (!match) return false;
          const periodNum = Number(match[1]);
          const weekInPeriod = Number(match[2]);
          const weekInYear = (periodNum - 1) * 4 + weekInPeriod;
          return weekInYear === row.weekInYear;
        })
      ) {
        return false;
      }

      // Delivery Date range filter
      if (
        appliedFilters.deliveryDateRange.start ||
        appliedFilters.deliveryDateRange.end
      ) {
        const rowDate = row.deliveryDate;
        if (
          appliedFilters.deliveryDateRange.start &&
          rowDate < appliedFilters.deliveryDateRange.start
        ) {
          return false;
        }
        if (
          appliedFilters.deliveryDateRange.end &&
          rowDate > appliedFilters.deliveryDateRange.end
        ) {
          return false;
        }
      }

      // Driver filter
      if (
        appliedFilters.selectedDrivers.length > 0 &&
        !appliedFilters.selectedDrivers.some(
          (d) =>
            String(d.displayText).toLowerCase() ===
            String(row.driver).toLowerCase(),
        )
      ) {
        return false;
      }

      // Status filter
      if (
        appliedFilters.selectedStatuses.length > 0 &&
        !appliedFilters.selectedStatuses.some(
          (s) =>
            String(s.displayText).toLowerCase() ===
            String(row.status).toLowerCase(),
        )
      ) {
        return false;
      }

      // On Time filter
      if (appliedFilters.selectedOnTime.length > 0) {
        const isOnTime = row.onTime;
        const hasOnTime = appliedFilters.selectedOnTime.some(
          (ot) => String(ot.displayText).toLowerCase() === "on time",
        );
        const hasLate = appliedFilters.selectedOnTime.some(
          (ot) => String(ot.displayText).toLowerCase() === "late",
        );
        if (isOnTime && !hasOnTime) return false;
        if (!isOnTime && !hasLate) return false;
      }

      // Arrived As Expected filter
      if (appliedFilters.selectedArrivedAsExpected.length > 0) {
        const aae = String(row.arrivedAsExpected);
        if (
          !appliedFilters.selectedArrivedAsExpected.some(
            (a) => String(a.displayText).toUpperCase() === aae.toUpperCase(),
          )
        ) {
          return false;
        }
      }

      // Manifest Hour range filter
      if (
        row.manifestHour < appliedFilters.manifestHourRange.from ||
        row.manifestHour > appliedFilters.manifestHourRange.to
      ) {
        return false;
      }

      // Load Search filter (searches across route IDs and location names)
      if (appliedFilters.loadSearch.trim()) {
        const searchTerm = appliedFilters.loadSearch.toLowerCase();
        const matchesId =
          String(row.localRouteId).toLowerCase().includes(searchTerm) ||
          String(row.nationalRouteId).toLowerCase().includes(searchTerm) ||
          String(row.id).toLowerCase().includes(searchTerm);
        const matchesLocation = String(row.location)
          .toLowerCase()
          .includes(searchTerm);
        if (!matchesId && !matchesLocation) {
          return false;
        }
      }

      return true;
    });
  }, [appliedFilters]);

  // Calculate KPI values based on filtered data
  const filteredKpiData = useMemo(() => {
    let plannedLoads = 0;
    let completedCount = 0;
    let onTimeCount = 0;
    let manifestArrivalNumerator = 0;
    let manifestArrivalDenominator = 0;

    filteredExecutionData.forEach((row) => {
      plannedLoads += 1;
      if (row.status === "Complete") {
        completedCount += 1;
        if (row.onTime) {
          onTimeCount += 1;
        }
      }
      if (row.manifestArrivalDateTime && row.manifestArrivalDateTime.trim()) {
        manifestArrivalDenominator += 1;
        if (row.arrivedAsExpected === "Y") {
          manifestArrivalNumerator += 1;
        }
      }
    });

    return {
      plannedLoads,
      completedLoads: completedCount,
      onTimeLoads: onTimeCount,
      incompleteLoads: plannedLoads - completedCount,
      manifestArrivalNumerator,
      manifestArrivalDenominator,
    };
  }, [filteredExecutionData]);

  const tabs = useMemo(
    () => [
      { key: "executive-overview" as const, label: "Executive Overview" },
      { key: "location-summary" as const, label: "Location Summary" },
      { key: "load-execution" as const, label: "Load Execution" },
    ],
    [],
  );

  // Location Summary → Executive Overview: pressing a Region/Unit, Market/POD,
  // or Location cell switches tabs and applies that single entity as the
  // corresponding filter, preserving every other currently applied filter.
  const handleLocationSummaryEntityNavigate = useCallback(
    (
      filterType: "region" | "market" | "location",
      item: { id: string; displayText: string },
    ) => {
      setActiveTab("executive-overview");
      if (filterType === "region") {
        const nextRegions = [item];
        setSelectedRegions(nextRegions);
        setAppliedFilters((prev) => ({ ...prev, selectedRegions: nextRegions }));
      } else if (filterType === "market") {
        const nextMarkets = [item];
        setSelectedMarkets(nextMarkets);
        setAppliedFilters((prev) => ({ ...prev, selectedMarkets: nextMarkets }));
      } else {
        const nextLocations = [item];
        setSelectedLocations(nextLocations);
        setAppliedFilters((prev) => ({ ...prev, selectedLocations: nextLocations }));
      }
    },
    [],
  );

  // Location Summary → Load Execution: pressing an Incomplete Loads value
  // (only rendered as a link when > 0) switches tabs and always sets Status
  // to Incomplete (not a toggle). When the row/card is at Location grain, the
  // corresponding Location filter is also applied. Both the scorecards and
  // load-execution detail requests read status from the same appliedFilters
  // snapshot, so updating selectedStatuses here keeps both API payloads and
  // the Status dropdown in sync automatically.
  const handleLocationSummaryIncompleteNavigate = useCallback(
    (location?: { id: string; displayText: string }) => {
      setActiveTab("load-execution");
      const nextStatuses = [INCOMPLETE_STATUS_FILTER_ITEM];
      setSelectedStatuses(nextStatuses);
      if (location) {
        const nextLocations = [location];
        setSelectedLocations(nextLocations);
        setAppliedFilters((prev) => ({
          ...prev,
          selectedStatuses: nextStatuses,
          selectedLocations: nextLocations,
        }));
      } else {
        setAppliedFilters((prev) => ({ ...prev, selectedStatuses: nextStatuses }));
      }
    },
    [],
  );

  return (
    <ScrollView
      style={[
        styles.container,
        isMobileIos && isDark && { backgroundColor: '#141414' },
        isDarkTablet && { backgroundColor: '#141414' },
      ]}
      contentContainerStyle={styles.contentContainer}
    >
      <View style={{ marginHorizontal: 24, marginTop: 12 }}>
        <DLOSimpleBreadcrumb
          path={["Analytics Hub", title]}
          onHomePress={() => {
            const nav = (globalThis as any)?.appNavigate;
            if (typeof nav === "function") {
              nav("Home");
              return;
            }

            if (Platform.OS === "web") {
              try {
                window.history.pushState({}, "", "/home");
                window.dispatchEvent(new PopStateEvent("popstate"));
              } catch {
                window.location.href = "/home";
              }
            }
          }}
          onNavigate={(index) => {
            if (index !== 0) return;
            const nav = (globalThis as any)?.appNavigate;
            if (typeof nav === "function") {
              nav("Analytics");
              return;
            }

            if (Platform.OS === "web") {
              try {
                window.history.pushState({}, "", "/analytics");
                window.dispatchEvent(new PopStateEvent("popstate"));
              } catch {
                window.location.href = "/analytics";
              }
            }
          }}
        />
      </View>

      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <View
            style={[
              styles.headerIconWrap,
              isMobileIos && isDark && { backgroundColor: '#1B2A3D' },
              isDarkTablet && { backgroundColor: '#1B2A3D' },
            ]}
          >
            <MaterialIcons
              name={iconName as any}
              size={26}
              color={(isMobileIos && isDark) || isDarkTablet ? '#3D8BFF' : '#005CBC'}
            />
          </View>
          <View>
            <Text style={[styles.title, isMobileIos && isDark && { color: '#F2F2F2' }, isDarkTablet && { color: '#F2F2F2' }]}>
              {title}
            </Text>
            <Text style={[styles.subtitle, isMobileIos && isDark && { color: '#B0B0B0' }, isDarkTablet && { color: '#B0B0B0' }]}>
              Dispatch completion, on-time performance, and driver adherence by
              location
            </Text>
          </View>
        </View>
        <View style={styles.refreshedBadge}>
          <Text style={[styles.refreshedBadgeText, isMobileIos && isDark && { color: '#B0B0B0' }, isDarkTablet && { color: '#B0B0B0' }]}>
            Refreshed:{" "}
            <Text style={[styles.refreshedBadgeValueText, isMobileIos && isDark && { color: '#3D8BFF' }, isDarkTablet && { color: '#3D8BFF' }]}>
              {latestUpdateDisplayValue}
            </Text>
          </Text>
        </View>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={[
          styles.tabBar,
          isMobileIos && isDark && { borderBottomColor: '#404040' },
          isDarkTablet && { borderBottomColor: '#404040' },
        ]}
        contentContainerStyle={styles.tabBarContent}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.tab,
                isActive && styles.tabActive,
                isActive && isMobileIos && isDark && { borderBottomColor: '#3D8BFF' },
                isActive && isDarkTablet && { borderBottomColor: '#3D8BFF' },
              ]}
              onPress={() => {
                setActiveTab(tab.key);
              }}
              activeOpacity={0.7}
            >
              <Text
                style={[
                  styles.tabLabel,
                  isMobileIos && isDark && { color: '#B0B0B0' },
                  isDarkTablet && { color: '#B0B0B0' },
                  isActive && styles.tabLabelActive,
                  isActive && isMobileIos && isDark && { color: '#3D8BFF' },
                  isActive && isDarkTablet && { color: '#3D8BFF' },
                ]}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* Shared global filter bar across all tabs */}
      <View
        style={[
          styles.filterBarWrapper,
          isMobileIos && styles.filterBarWrapperMobileHidden,
        ]}
      >
        <View style={[styles.filterBar, isDarkTablet && { backgroundColor: '#1C1C1C', borderColor: '#363636' }]}>
          <View style={styles.filterBarContent}>
            {/* Filters + actions */}
            <View style={styles.filterBarLeft}>
              <View style={styles.filtersRow}>
                <View style={styles.filterItem}>
                  <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>REGION / UNIT</Text>
                  <KPIFilterDropdown
                    childList={regionOptions}
                    selectedValue={selectedRegions}
                    setSelectedValue={setSelectedRegions}
                    placeholder="All"
                    isOpen={openFilterKey === "region"}
                    onOpenChange={(open) =>
                      setOpenFilterKey(open ? "region" : null)
                    }
                    testID="otd-filter-region"
                  />
                </View>

                <View style={styles.filterItem}>
                  <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>MARKET / POD</Text>
                  <KPIFilterDropdown
                    childList={regionFilteredMarkets}
                    selectedValue={selectedMarkets}
                    setSelectedValue={setSelectedMarkets}
                    placeholder="All"
                    isOpen={openFilterKey === "market"}
                    onOpenChange={(open) =>
                      setOpenFilterKey(open ? "market" : null)
                    }
                    testID="otd-filter-market"
                  />
                </View>

                <View style={styles.filterItem}>
                  <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>LOCATION</Text>
                  <KPIFilterDropdown
                    childList={locationOptions}
                    selectedValue={selectedLocations}
                    setSelectedValue={setSelectedLocations}
                    placeholder="All"
                    isOpen={openFilterKey === "location"}
                    onOpenChange={(open) =>
                      setOpenFilterKey(open ? "location" : null)
                    }
                    testID="otd-filter-location"
                  />
                </View>

                <View style={styles.filterItem}>
                  <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>LOCATION TYPE</Text>
                  <KPIFilterDropdown
                    childList={locationTypeOptions}
                    selectedValue={selectedLocationTypes}
                    setSelectedValue={setSelectedLocationTypes}
                    placeholder="All"
                    isOpen={openFilterKey === "locationType"}
                    onOpenChange={(open) =>
                      setOpenFilterKey(open ? "locationType" : null)
                    }
                    testID="otd-filter-location-type"
                  />
                </View>

                <View style={styles.filterItem}>
                  <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>YEAR</Text>
                  <KPIFilterDropdown
                    childList={yearOptions}
                    selectedValue={selectedYears}
                    setSelectedValue={(value) => {
                      // Manual edit — deselect any active Timeframe preset
                      // chip/pill while preserving this new Year selection
                      // (and the current Period/Week values, untouched here).
                      clearActiveQuickSelect();
                      setSelectedYears(value);
                    }}
                    placeholder="All"
                    isOpen={openFilterKey === "year"}
                    onOpenChange={(open) =>
                      setOpenFilterKey(open ? "year" : null)
                    }
                    testID="otd-filter-year"
                  />
                </View>

                <View style={styles.filterItem}>
                  <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>PERIOD</Text>
                  <KPIFilterDropdown
                    childList={periodOptions}
                    selectedValue={selectedPeriods}
                    setSelectedValue={(value) => {
                      // Manual edit — deselect any active Timeframe preset
                      // chip/pill while preserving this new Period selection
                      // (and the current Year/Week values, untouched here).
                      clearActiveQuickSelect();
                      setSelectedPeriods(value);
                    }}
                    placeholder="All"
                    isOpen={openFilterKey === "period"}
                    onOpenChange={(open) =>
                      setOpenFilterKey(open ? "period" : null)
                    }
                    testID="otd-filter-period"
                    disabledIds={disabledPeriodIds}
                  />
                </View>

                <View style={[styles.filterItem, styles.weekInYearFilterItem]}>
                  <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>WEEK IN YEAR</Text>
                  <KPIFilterDropdown
                    childList={weekOptions}
                    selectedValue={selectedWeeks}
                    setSelectedValue={(value) => {
                      // Manual edit — deselect any active Timeframe preset
                      // chip/pill while preserving this new Week selection
                      // (and the current Year/Period values, untouched here).
                      clearActiveQuickSelect();
                      setSelectedWeeks(value);
                    }}
                    placeholder="All"
                    isOpen={openFilterKey === "week"}
                    onOpenChange={(open) =>
                      setOpenFilterKey(open ? "week" : null)
                    }
                    testID="otd-filter-week"
                    disabledIds={disabledWeekIds}
                  />
                </View>

                <View
                  style={[styles.filterItem, styles.deliveryDateFilterItem]}
                >
                  <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>DELIVERY DATE</Text>
                  <DeliveryDateRangeFilter
                    value={deliveryDateRange}
                    onChange={setDeliveryDateRange}
                    mode="single"
                    singleDatePlaceholder="mm-dd-yyyy"
                    testID="otd-filter-delivery-date"
                  />
                </View>

                <View style={styles.filterActionItem}>
                  <View style={styles.filterActionLabelSpacer} />
                  <View style={styles.filterActions}>
                    <TouchableOpacity
                      style={[styles.resetBtn, isDarkTablet && { backgroundColor: '#252525', borderColor: '#3D8BFF' }]}
                      onPress={handleResetFilters}
                    >
                      <Text style={[styles.resetBtnText, isDarkTablet && { color: '#3D8BFF' }]}>Reset</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.applyBtn,
                        hasPendingChanges
                          ? styles.applyBtnEnabled
                          : styles.applyBtnDisabled,
                      ]}
                      onPress={hasPendingChanges ? handleApplyFilters : undefined}
                      disabled={!hasPendingChanges}
                    >
                      <Text
                        style={
                          hasPendingChanges
                            ? styles.applyBtnTextEnabled
                            : styles.applyBtnText
                        }
                      >
                        Apply
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {/* <View style={styles.filterDivider} /> */}

              {IS_LOAD_EXECUTION_ENABLED && activeTab === "load-execution" && (
                <>
                  <View style={styles.loadFiltersRow}>
                    <View style={styles.filterItem}>
                      <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>DRIVER</Text>
                      <KPIFilterDropdown
                        childList={driverOptions}
                        selectedValue={selectedDrivers}
                        setSelectedValue={setSelectedDrivers}
                        placeholder="All"
                        isOpen={openFilterKey === "driver"}
                        onOpenChange={(open) =>
                          setOpenFilterKey(open ? "driver" : null)
                        }
                        testID="otd-filter-driver"
                      />
                    </View>

                    <View style={styles.filterItem}>
                      <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>STATUS</Text>
                      <KPIFilterDropdown
                        childList={loadStatusOptions}
                        selectedValue={selectedStatuses}
                        setSelectedValue={setSelectedStatuses}
                        placeholder="All"
                        isOpen={openFilterKey === "status"}
                        onOpenChange={(open) =>
                          setOpenFilterKey(open ? "status" : null)
                        }
                        testID="otd-filter-status"
                      />
                    </View>

                    <View style={styles.filterItem}>
                      <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>ON-TIME</Text>
                      <KPIFilterDropdown
                        childList={loadOnTimeOptions}
                        selectedValue={selectedOnTime}
                        setSelectedValue={setSelectedOnTime}
                        placeholder="All"
                        isOpen={openFilterKey === "onTime"}
                        onOpenChange={(open) =>
                          setOpenFilterKey(open ? "onTime" : null)
                        }
                        testID="otd-filter-on-time"
                      />
                    </View>

                    <View style={styles.filterItem}>
                      <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>
                        ARRIVED AS EXPECTED
                      </Text>
                      <KPIFilterDropdown
                        childList={loadArrivedExpectedOptions}
                        selectedValue={selectedArrivedAsExpected}
                        setSelectedValue={setSelectedArrivedAsExpected}
                        placeholder="All"
                        isOpen={openFilterKey === "arrivedExpected"}
                        onOpenChange={(open) =>
                          setOpenFilterKey(open ? "arrivedExpected" : null)
                        }
                        testID="otd-filter-arrived-expected"
                      />
                    </View>

                    <View
                      style={[styles.filterItem, styles.manifestHourRangeItem]}
                    >
                      <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>
                        MANIFEST HOUR RANGE
                      </Text>
                      <View style={[styles.manifestHourRangeWrap, isDarkTablet && { backgroundColor: '#252525' }]}>
                        <View style={styles.manifestHourSelectWrap}>
                          <KPIFilterDropdown
                            childList={manifestHourOptions}
                            selectedValue={manifestHourFromSelection}
                            setSelectedValue={(items) => {
                              const parsed = Number(items[0]?.id ?? 0);
                              const next = Math.max(0, Math.min(23, parsed));
                              setManifestHourFrom(next);
                              if (next > manifestHourTo)
                                setManifestHourTo(next);
                            }}
                            placeholder="00"
                            singleSelect
                            testID="otd-filter-manifest-from"
                          />
                        </View>
                        <Text style={[styles.manifestHourDash, isDarkTablet && { color: '#B0B0B0' }]}>-</Text>
                        <View style={styles.manifestHourSelectWrap}>
                          <KPIFilterDropdown
                            childList={manifestHourOptions}
                            selectedValue={manifestHourToSelection}
                            setSelectedValue={(items) => {
                              const parsed = Number(items[0]?.id ?? 23);
                              const next = Math.max(0, Math.min(23, parsed));
                              setManifestHourTo(next);
                              if (next < manifestHourFrom)
                                setManifestHourFrom(next);
                            }}
                            placeholder="23"
                            singleSelect
                            testID="otd-filter-manifest-to"
                          />
                        </View>
                      </View>
                    </View>

                    <View style={[styles.filterItem, styles.loadSearchItem]}>
                      <Text style={[styles.filterLabel, isDarkTablet && { color: '#B0B0B0' }]}>SEARCH</Text>
                      <View style={[styles.loadSearchWrap, isDarkTablet && { backgroundColor: '#252525', borderColor: '#404040' }]}>
                        <MaterialIcons
                          name="search"
                          size={16}
                          color={isDarkTablet ? '#6B7280' : '#9CA3AF'}
                          style={styles.loadSearchIcon}
                        />
                        <TextInput
                          value={loadSearch}
                          onChangeText={setLoadSearch}
                          placeholder="Dispatch ID, route, driver, location"
                          placeholderTextColor={isDarkTablet ? '#6B7280' : '#9CA3AF'}
                          style={[styles.loadSearchInput, isDarkTablet && { color: '#F2F2F2' }]}
                        />
                      </View>
                    </View>
                  </View>
                </>
              )}
            </View>
          </View>

          <View style={[styles.filterDividerSecondary, isDarkTablet && { backgroundColor: '#363636' }]} />

          <View style={styles.timeframeRow}>
            <Text style={[styles.timeframeLabel, isDarkTablet && { color: '#B0B0B0' }]}>TIMEFRAME</Text>
            <View style={styles.quickSelectRow}>
              {QUICK_SELECT_OPTIONS.map((opt) => {
                const isActive = activeQuickSelect === opt.key;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    style={[
                      styles.quickSelectPill,
                      isDarkTablet && { backgroundColor: '#252525', borderColor: '#404040' },
                      isActive && styles.quickSelectPillActive,
                      isActive && isDarkTablet && { backgroundColor: '#3D8BFF', borderColor: '#3D8BFF' },
                    ]}
                    onPress={() => handleQuickSelectPress(opt.key)}
                  >
                    <Text
                      style={[
                        styles.quickSelectPillText,
                        isDarkTablet && { color: '#B0B0B0' },
                        isActive && styles.quickSelectPillTextActive,
                      ]}
                    >
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </View>
      </View>

      {activeTab === "executive-overview" && (
        <ExecutiveOverviewTab
          appliedFilters={appliedFilters}
          adminLocations={adminLocations}
          kpiData={kpiData}
          kpiLoading={kpiLoading}
          kpiError={kpiError}
          onKpiRetry={refetchKpi}
          filteredKpiData={filteredKpiData}
          isDarkTablet={isDarkTablet}
        />
      )}

      <View style={styles.tabContentContainer}>
        {activeTab === "location-summary" && (
          <LocationSummaryTab
            appliedFilters={appliedFilters}
            adminLocations={adminLocations}
            locationTableColumnConfig={locationTableColumnConfig}
            setLocationTableColumnConfig={setLocationTableColumnConfig}
            locationTableColumnSectionState={locationTableColumnSectionState}
            setLocationTableColumnSectionState={setLocationTableColumnSectionState}
            isDarkTablet={isDarkTablet}
            onNavigateToExecutiveOverview={handleLocationSummaryEntityNavigate}
            onNavigateToLoadExecutionIncomplete={handleLocationSummaryIncompleteNavigate}
          />
        )}

        {activeTab === "load-execution" &&
          (IS_LOAD_EXECUTION_ENABLED ? (
            <LoadExecution
              rows={filteredLoadExecutionRows}
              detailRecords={detailRecords}
              detailLoading={detailLoading}
              detailError={detailError}
              totalElements={totalElements}
              pageNumber={pageNumber}
              pageSize={pageSize}
              onPageChange={setPageNumber}
              onPageSizeChange={setPageSize}
              onSort={setSorting}
              sortBy={sortBy}
              sortDirection={sortDirection}
              scorecardData={scorecardData}
              scorecardLoading={scorecardLoading}
              scorecardError={scorecardError}
              appliedFilters={normalizedAppliedFilters}
              adminLocations={adminLocations}
              isDarkTablet={isDarkTablet}
            />
          ) : (
            <View style={[styles.placeholderCard, isDarkTablet && { backgroundColor: '#202020', borderColor: '#404040' }]}>
              <Text style={[styles.placeholderTitle, isDarkTablet && { color: '#F2F2F2' }]}>Load Execution</Text>
              <Text style={[styles.placeholderText, isDarkTablet && { color: '#B0B0B0' }]}>
                This tab is temporarily isolated while the runtime issue is
                being resolved.
              </Text>
              <Text style={[styles.placeholderMeta, isDarkTablet && { color: '#3D8BFF' }]}>
                Executive Overview and Location Summary remain available.
              </Text>
            </View>
          ))}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f9ff",
  },
  contentContainer: {
    paddingBottom: 40,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: "#DDE8F7",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "700",
    fontFamily: FontFamily.bold,
    color: "#1A1D21",
  },
  subtitle: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: "#6B7280",
    marginTop: 2,
  },
  refreshedBadge: {
    flexDirection: "row",
    alignItems: "center",
  },
  refreshedBadgeText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: "#6B7280",
  },
  refreshedBadgeValueText: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    color: "#005CBC",
  },
  tabBar: {
    marginHorizontal: 24,
    marginTop: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  tabBarContent: {
    flexDirection: "row",
    alignItems: "center",
    columnGap: 12,
    paddingBottom: 0,
  },
  tab: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  tabActive: {
    borderBottomColor: "#005CBC",
  },
  tabLabel: {
    fontSize: 14,
    color: "#6B7280",
    fontFamily: FontFamily.medium,
    fontWeight: "700",
  },
  tabLabelActive: {
    color: "#005CBC",
    fontFamily: FontFamily.bold,
    fontWeight: "700",
  },
  filterBarWrapper: {
    paddingHorizontal: 24,
    paddingTop: 4,
    paddingBottom: 16,
  },
  filterBarWrapperMobileHidden: {
    display: "none",
  },
  filterBar: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E3E9F3",
    padding: 20,
    shadowColor: "#A8B4C5",
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  filterBarContent: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 16,
  },
  filterBarLeft: {
    flex: 1,
    flexDirection: "column",
    gap: 16,
    minWidth: 0,
  },
  filterBarRight: {
    flexShrink: 0,
    justifyContent: "flex-end",
    alignItems: "flex-end",
  },
  filtersRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 12,
  },
  filterItem: {
    minWidth: 140,
    flexGrow: 1,
  },
  deliveryDateFilterItem: {
    minWidth: 140,
    maxWidth: 220,
    flexGrow: 0,
    flexShrink: 0,
  },
  weekInYearFilterItem: {
    minWidth: 140,
    maxWidth: 220,
    flexGrow: 0,
    flexShrink: 0,
  },
  filterActionItem: {
    minWidth: 190,
    flexGrow: 0,
    flexShrink: 0,
    marginLeft: "auto",
    alignSelf: "flex-end",
  },
  filterActionLabelSpacer: {
    height: 22,
    marginBottom: 8,
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: "700",
    fontFamily: FontFamily.bold,
    color: "#6A6D71",
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  filterActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    justifyContent: "flex-end",
    flexWrap: "nowrap",
  },
  moreFiltersBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 6,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
  },
  moreFiltersBtnText: {
    fontSize: 13,
    color: "#0B5FC6",
    fontFamily: FontFamily.bold,
  },
  moreFiltersCountPill: {
    minWidth: 18,
    height: 18,
    borderRadius: 999,
    paddingHorizontal: 5,
    backgroundColor: "#0B5FC6",
    alignItems: "center",
    justifyContent: "center",
  },
  moreFiltersCountText: {
    fontSize: 10,
    color: "#FFFFFF",
    fontFamily: FontFamily.bold,
  },
  filterDivider: {
    height: 1,
    backgroundColor: "#E3E9F3",
    marginTop: 16,
    marginBottom: 16,
  },
  filterDividerSecondary: {
    height: 1,
    backgroundColor: "#E3E9F3",
    marginTop: 14,
    marginBottom: 14,
  },
  timeframeRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
  },
  timeframeLabel: {
    fontSize: 12,
    fontWeight: "700",
    fontFamily: FontFamily.bold,
    color: "#6A6D71",
    letterSpacing: 0.4,
  },
  quickSelectRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 0,
    flex: 1,
    minHeight: 32,
  },
  quickSelectPill: {
    borderWidth: 1,
    borderColor: "#E3E9F3",
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: "#FFFFFF",
  },
  quickSelectPillActive: {
    backgroundColor: "#0B5FC6",
    borderColor: "#0B5FC6",
  },
  quickSelectPillText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: "#3D4147",
  },
  quickSelectPillTextActive: {
    color: "#FFFFFF",
    fontFamily: FontFamily.bold,
  },
  loadFiltersRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 12,
    marginTop: 20,
  },
  manifestHourRangeItem: {
    minWidth: 220,
    maxWidth: 260,
  },
  manifestHourRangeWrap: {
    height: 40,
    backgroundColor: "#FFFFFF",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 8,
  },
  manifestHourSelectWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    position: "relative",
    paddingHorizontal: 4,
  },
  manifestHourValueText: {
    fontSize: 13,
    color: "#1F2937",
    fontFamily: FontFamily.medium,
    minWidth: 18,
    textAlign: "center",
  },
  manifestHourDash: {
    color: "#98A2B3",
    fontSize: 14,
    fontFamily: FontFamily.bold,
    marginHorizontal: 2,
  },
  manifestHourHiddenInput: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    opacity: 0,
  },
  loadSearchItem: {
    minWidth: 260,
    maxWidth: 320,
    flexGrow: 1.2,
  },
  loadSearchWrap: {
    height: 40,
    borderWidth: 1,
    borderColor: "#DEE2E6",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
  },
  loadSearchIcon: {
    marginRight: 6,
  },
  loadSearchInput: {
    flex: 1,
    fontSize: 13,
    color: "#1F2937",
    fontFamily: FontFamily.medium,
    paddingVertical: 0,
  },
  resetBtn: {
    borderWidth: 1,
    borderColor: "#0B5FC6",
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 24,
    backgroundColor: "#FFFFFF",
    minHeight: 40,
    minWidth: 88,
    alignItems: "center",
    justifyContent: "center",
  },
  resetBtnText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: "#0B5FC6",
  },
  applyBtn: {
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 24,
    minHeight: 40,
    minWidth: 88,
    alignItems: "center",
    justifyContent: "center",
  },
  applyBtnEnabled: {
    backgroundColor: "#0B5FC6",
  },
  applyBtnDisabled: {
    backgroundColor: "#D0D5DD",
  },
  applyBtnText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: "#9CA3AF",
  },
  applyBtnTextEnabled: {
    color: "#FFFFFF",
  },
  tabContentContainer: {
    paddingHorizontal: 24,
    paddingTop: 10,
    paddingBottom: 10,
  },
  placeholderCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 20,
    shadowColor: "#A8B4C5",
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  placeholderTitle: {
    fontSize: 18,
    fontFamily: FontFamily.bold,
    color: "#1F2937",
    marginBottom: 8,
  },
  placeholderText: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: "#4B5563",
    lineHeight: 20,
  },
  placeholderMeta: {
    marginTop: 10,
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: "#005CBC",
  },
  inlineBadge: {
    marginTop: 12,
    borderRadius: 8,
    backgroundColor: "#FEF3F2",
    borderWidth: 1,
    borderColor: "#FECACA",
    paddingHorizontal: 12,
    paddingVertical: 8,
    alignSelf: "flex-start",
  },
  inlineBadgeText: {
    fontSize: 12,
    color: "#9F1239",
    fontFamily: FontFamily.medium,
  },
});

export default OnTimeDispatchDashboard;
